import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { NotificationRecord } from './useData';
import { formatDateTime, APP_CONFIG } from '../../config/appConfig';
import { Badge, StandardTableActions } from '../../components/ui';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { getNotificationType, NOTIFICATION_TYPES } from '../../types/notifications';

export const moduleConfig = {
  module: {
    title: 'Notifications',
    description: 'View and edit notification records',
    permissionModuleKey: 'notifications',
    route: {
      list: '/notifications',
      new: '/notifications/new',
      detail: (id: number) => `/notifications/${id}`,
      edit: (id: number) => `/notifications/${id}/edit`,
    },
    api: {
      list: '/notifications',
      detail: (id: number) => `/notifications/${id}`,
    },
  },

  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  search: {
    placeholder: 'Search by message or entity type...',
  },

  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['entityType', 'read'] },
  ],

  filters: {
    fields: [
      {
        key: 'entityType',
        label: 'Type',
        type: 'select' as const,
        placeholder: 'All types',
        getValue: (item: NotificationRecord) => item.entityType,
        options: Object.entries(NOTIFICATION_TYPES).map(([value, config]) => ({
          value,
          label: config.label,
        })),
      },
      {
        key: 'read',
        label: 'Status',
        type: 'select' as const,
        placeholder: 'All',
        getValue: (item: NotificationRecord) => (item.readAt ? 'read' : 'unread'),
        options: [
          { value: 'read', label: 'Read' },
          { value: 'unread', label: 'Unread' },
        ],
      },
    ],
    applyFilter: (
      items: NotificationRecord[],
      filters: { entityType?: string[]; read?: string[] }
    ) => {
      return items.filter((item) => {
        if (filters.entityType?.length && !filters.entityType.includes(getNotificationType(item.entityType))) return false;
        if (filters.read?.length) {
          const isRead = !!item.readAt;
          const wantRead = filters.read.includes('read');
          const wantUnread = filters.read.includes('unread');
          if (wantRead && !wantUnread && !isRead) return false;
          if (wantUnread && !wantRead && isRead) return false;
        }
        return true;
      });
    },
  },

  toolbar: {
    actions: [],
    download: { enabled: true, formats: ['csv', 'excel', 'pdf'] },
  },

  table: {
    actionsPosition: 'right' as const,
    onRowClick: (item: NotificationRecord) => {
      window.location.href = `/notifications/${item.id}/edit`;
    },
    keyExtractor: (item: NotificationRecord) => item.id,
  },

  emptyStates: {
    noData: 'No notifications.',
    noSearchResults: 'No notifications match your search.',
    noFilterResults: 'No notifications match the selected filters.',
  },

  delete: {
    title: 'Delete notification',
    message: 'Are you sure you want to delete this notification?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete notification.',
    handler: async (id: number) => {
      const api = (await import('../../services/api')).default;
      await api.delete(`/notifications/${id}`);
    },
  },

  export: { enabled: true, formats: ['csv', 'excel', 'pdf'] },

  utils: { formatDate: (d?: string) => (d ? formatDateTime(d) : '') },

  fields: [
    { name: 'message', label: 'Message', type: 'textarea' },
    { name: 'link', label: 'Link', type: 'text' },
    { name: 'readAt', label: 'Mark as read', type: 'checkbox' },
  ] as FieldConfig[],
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<NotificationRecord>[] => [
  {
    key: 'entityType',
    header: 'Type',
    sortable: true,
    render: (_, item) => {
      const type = getNotificationType(item.entityType);
      const config = NOTIFICATION_TYPES[type];
      return (
        <Badge variant="secondary">
          {config?.label ?? item.entityType}
        </Badge>
      );
    },
  },
  {
    key: 'message',
    header: 'Message',
    sortable: true,
    render: (value) => (
      <span className="line-clamp-2 max-w-md" title={value}>
        {value}
      </span>
    ),
  },
  {
    key: 'link',
    header: 'Link',
    sortable: false,
    render: (value) =>
      value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline truncate max-w-xs block">
          {value}
        </a>
      ) : (
        <span className="text-gray-400">—</span>
      ),
  },
  {
    key: 'readAt',
    header: 'Status',
    sortable: true,
    render: (_, item) =>
      item.readAt ? (
        <Badge variant="accent">Read</Badge>
      ) : (
        <Badge variant="warning">Unread</Badge>
      ),
  },
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (_, item) => formatDate(item.createdAt),
  },
];

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
};

export const getTableActions = (
  item: NotificationRecord,
  onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      editTo={`/notifications/${item.id}/edit`}
      onDelete={() => onDelete(item.id)}
      canView={false}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};
